<template>
  <div class="p-6 space-y-6 text-slate-900 dark:text-white">
    <!-- Header -->
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-black">{{ $t("add_car") }}</h1>
        <p
          class="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1"
        >
          {{ $t("basic_info") }}
        </p>
      </div>
      <BaseButton
        type="secondary"
        @click="$router.back()"
        class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
      >
        {{ $t("back") }}
      </BaseButton>
    </div>

    <div class="grid lg:grid-cols-3 gap-6">
      <!-- Left Column: Primary Data -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Basic Info -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4 transition-colors"
        >
          <h2
            class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
          >
            {{ $t("basic_info") }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("model_name") }}</label
              >
              <input
                v-model="form.model_name"
                type="text"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                placeholder="e.g. Kia Cerato"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("year") }}</label
              >
              <input
                v-model="form.year"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                placeholder="2022"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("color") }}</label
              >
              <input
                v-model="form.color"
                type="text"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                placeholder="Red"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("plate_number") }}</label
              >
              <input
                v-model="form.plate_number"
                type="text"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                placeholder="MNO-7890"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("seats") }}</label
              >
              <input
                v-model="form.number_of_seats"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("doors") }}</label
              >
              <input
                v-model="form.doors"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("fuel_efficiency") }}</label
              >
              <input
                v-model="form.fuel_efficiency"
                type="text"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
                placeholder="16 km/l"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("fuel_policy") }}</label
              >
              <select
                v-model="form.fuel_policy"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              >
                <option value="same_level">{{ $t("same_level") }}</option>
                <option value="prepaid">{{ $t("prepaid") }}</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("status") }}</label
              >
              <select
                v-model="form.status"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              >
                <option value="available">{{ $t("available") }}</option>
                <option value="rented">{{ $t("rented") }}</option>
                <option value="maintenance">{{ $t("maintenance") }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Pricing & Usage -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4 transition-colors"
        >
          <h2
            class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
          >
            {{ $t("pricing_usage") }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("currency") }}</label
              >
              <select
                v-model="form.currency"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-black text-primary text-sm"
              >
                <option value="EGP">EGP</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("price_day") }} ({{ form.currency }})</label
              >
              <input
                v-model="form.price_per_day"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-black text-primary text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("price_week") }} ({{ form.currency }})</label
              >
              <input
                v-model="form.price_per_week"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-black text-primary text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("price_month") }} ({{ form.currency }})</label
              >
              <input
                v-model="form.price_per_month"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-black text-primary text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("miles_day") }}</label
              >
              <input
                v-model="form.miles_per_day"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("extra_mile_fee") }} ({{ form.currency }})</label
              >
              <input
                v-model="form.extra_mile_fee"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("delivery_fee_per_km") }} ({{ form.currency }})</label
              >
              <input
                v-model="form.delivery_fee_per_km"
                type="number"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2.5 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all font-bold text-sm"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label
            class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
          >
            {{ $t("insurance_amount") }} ({{ form.currency }})
          </label>
          <input
            v-model="form.security_deposit"
            type="number"
            class="bg-slate-50 dark:bg-dark-input border border-slate-200 p-2.5 rounded-xl w-full"
            placeholder="e.g. 500"
          />
        </div>

        <div class="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            v-model="form.has_driver"
            class="w-4 h-4 accent-primary"
          />
          <label class="text-xs font-bold text-slate-600">
            {{ $t("has_driver") }}
          </label>
        </div>

        <!-- Location & Availability -->
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Location -->
          <div
            class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h2
              class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
            >
              {{ $t("location") }}
            </h2>
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("country") }}</label
                >
                <select
                  v-model="form.location.country_id"
                  @change="filterCities"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs outline-none"
                >
                  <option value="">{{ $t("select_country") }}</option>
                  <option v-for="c in countries" :key="c.id" :value="c.id">
                    {{ $i18n.locale === "ar" ? c.name_ar : c.name_en }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("city") }}</label
                >
                <select
                  v-model="form.location.city_id"
                  :disabled="!form.location.country_id"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs outline-none disabled:opacity-50"
                >
                  <option value="">{{ $t("select_city") }}</option>
                  <option v-for="c in cities" :key="c.id" :value="c.id">
                    {{ $i18n.locale === "ar" ? c.name_ar : c.name_en }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("state") }}</label
                >
                <input
                  v-model="form.location.state"
                  type="text"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("postal_code") }}</label
                >
                <input
                  v-model="form.location.postal_code"
                  type="text"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                />
              </div>

              <!-- Street Address with Autocomplete -->
              <div class="flex flex-col gap-1 col-span-2 relative">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("street_address") }}</label
                >
                <input
                  v-model="form.location.street_address"
                  type="text"
                  autocomplete="off"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                  @input="onStreetAddressInput"
                  @blur="onStreetAddressBlur"
                />
                <!-- Suggestions Dropdown -->
                <div
                  v-if="showSuggestions && suggestions.length > 0"
                  class="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-dark-surface border border-slate-200 dark:border-white/10 rounded-xl shadow-xl z-50 overflow-hidden"
                >
                  <ul>
                    <li
                      v-for="(item, index) in suggestions"
                      :key="index"
                      @mousedown.prevent="selectSuggestion(item)"
                      class="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors"
                    >
                      <i
                        class="fas fa-map-marker-alt text-primary mt-0.5 text-xs flex-shrink-0"
                      ></i>
                      <span
                        class="text-xs text-slate-700 dark:text-slate-200 leading-tight"
                        >{{ item.display_name }}</span
                      >
                    </li>
                  </ul>
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("latitude") }}</label
                >
                <input
                  v-model="form.location.latitude"
                  type="text"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                  readonly
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("longitude") }}</label
                >
                <input
                  v-model="form.location.longitude"
                  type="text"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                  readonly
                />
              </div>
            </div>

            <!-- Map Picker -->
            <div class="mt-4 space-y-2">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest block"
                >{{ $t("select_location_on_map") }}</label
              >
              <div
                id="map"
                class="h-64 rounded-xl border border-slate-200 dark:border-white/5 z-0"
              ></div>
            </div>
          </div>

          <!-- Availability -->
          <div
            class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h2
              class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
            >
              {{ $t("availability") }}
            </h2>
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("advance_notice") }}</label
                >
                <input
                  v-model="form.availability.advance_notice_hours"
                  type="number"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("min_trip_days") }}</label
                >
                <input
                  v-model="form.availability.minimum_trip_days"
                  type="number"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("max_trip_days") }}</label
                >
                <input
                  v-model="form.availability.max_trip_days"
                  type="number"
                  class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-2 rounded-lg text-xs"
                />
              </div>
              <div class="flex flex-col gap-2 col-span-2">
                <label
                  class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                  >{{ $t("available_days") }}</label
                >
                <div class="flex flex-wrap gap-1">
                  <button
                    v-for="day in daysList"
                    :key="day"
                    type="button"
                    @click="toggleDay(day)"
                    :class="
                      form.availability.available_days.includes(day)
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 dark:bg-dark-input text-slate-500'
                    "
                    class="px-2 py-1 rounded text-[9px] font-bold uppercase transition-all"
                  >
                    {{ day.substring(0, 3) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Multi-Selects (Features, Rules, Pickup, Experiences) -->
        <div class="grid md:grid-cols-2 gap-6">
          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <div class="flex justify-between items-center">
              <h3
                class="text-[10px] font-black uppercase tracking-widest text-primary"
              >
                {{ $t("features") }}
              </h3>

              <BaseButton
                type="primary"
                @click="toggleSelectAll('featuresList', 'features')"
                class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
              >
                {{
                  isAllSelected("featuresList", "features")
                    ? $t("deselect_all")
                    : $t("select_all")
                }}
              </BaseButton>
            </div>

            <div class="flex flex-wrap gap-2">
              <label
                v-for="f in featuresList"
                :key="f.id"
                class="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  :value="f.id"
                  v-model="form.features"
                  class="hidden"
                />
                <span
                  :class="
                    form.features.includes(f.id)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-slate-50 dark:bg-dark-input border-slate-200 dark:border-white/10 text-slate-500'
                  "
                  class="px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase transition-all group-hover:scale-105"
                >
                  {{ $i18n.locale === "ar" ? f.name_ar : f.name_en }}
                </span>
              </label>
            </div>
          </div>

          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h3
              class="text-[10px] font-black uppercase tracking-widest text-primary"
            >
              {{ $t("car_rules") }}
            </h3>

            <BaseButton
              type="primary"
              @click="toggleSelectAll('rulesList', 'rules')"
              class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
            >
              {{
                isAllSelected("rulesList", "rules")
                  ? $t("deselect_all")
                  : $t("select_all")
              }}
            </BaseButton>

            <div class="flex flex-wrap gap-2">
              <label
                v-for="r in rulesList"
                :key="r.id"
                class="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  :value="r.id"
                  v-model="form.rules"
                  class="hidden"
                />
                <span
                  :class="
                    form.rules.includes(r.id)
                      ? 'bg-red-500 text-white border-red-500'
                      : 'bg-slate-50 dark:bg-dark-input border-slate-200 dark:border-white/10 text-slate-500'
                  "
                  class="px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase transition-all group-hover:scale-105"
                >
                  {{ $i18n.locale === "ar" ? r.name_ar : r.name_en }}
                </span>
              </label>
            </div>
          </div>

          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h3
              class="text-[10px] font-black uppercase tracking-widest text-primary"
            >
              {{ $t("pickup_options") }}
            </h3>

            <BaseButton
              type="primary"
              @click="toggleSelectAll('pickupOptionsList', 'pickup_options')"
              class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
            >
              {{
                isAllSelected("pickupOptionsList", "pickup_options")
                  ? $t("deselect_all")
                  : $t("select_all")
              }}
            </BaseButton>

            <div class="flex flex-wrap gap-2">
              <label
                v-for="p in pickupOptionsList"
                :key="p.id"
                class="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  :value="p.id"
                  v-model="form.pickup_options"
                  class="hidden"
                />
                <span
                  :class="
                    form.pickup_options.includes(p.id)
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-slate-50 dark:bg-dark-input border-slate-200 dark:border-white/10 text-slate-500'
                  "
                  class="px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase transition-all group-hover:scale-105"
                >
                  {{ $i18n.locale === "ar" ? p.name_ar : p.name_en }}
                </span>
              </label>
            </div>
          </div>

          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h3
              class="text-[10px] font-black uppercase tracking-widest text-primary"
            >
              {{ $t("elevate_experiences") }}
            </h3>

            <BaseButton
              type="primary"
              @click="
                toggleSelectAll('elevateExperiencesList', 'elevate_experiences')
              "
              class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
            >
              {{
                isAllSelected("elevateExperiencesList", "elevate_experiences")
                  ? $t("deselect_all")
                  : $t("select_all")
              }}
            </BaseButton>

            <div class="flex flex-wrap gap-2">
              <label
                v-for="e in elevateExperiencesList"
                :key="e.id"
                class="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  :value="e.id"
                  v-model="form.elevate_experiences"
                  class="hidden"
                />
                <span
                  :class="
                    form.elevate_experiences.includes(e.id)
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-slate-50 dark:bg-dark-input border-slate-200 dark:border-white/10 text-slate-500'
                  "
                  class="px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase transition-all group-hover:scale-105"
                >
                  {{ $i18n.locale === "ar" ? e.name_ar : e.name_en }}
                </span>
              </label>
            </div>
          </div>

          <div
            class="bg-white dark:bg-dark-surface shadow rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4"
          >
            <h3
              class="text-[10px] font-black uppercase tracking-widest text-primary"
            >
              {{ $t("extras") }}
            </h3>

            <BaseButton
              type="primary"
              @click="toggleSelectAll('extrasList', 'extras')"
              class="!rounded-xl border-slate-200 dark:border-white/5 bg-white dark:bg-dark-surface"
            >
              {{
                isAllSelected("extrasList", "extras")
                  ? $t("deselect_all")
                  : $t("select_all")
              }}
            </BaseButton>

            <div class="flex flex-wrap gap-2">
              <label
                v-for="ex in extrasList"
                :key="ex.id"
                class="flex items-center gap-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  :value="ex.id"
                  v-model="form.extras"
                  class="hidden"
                />
                <span
                  :class="
                    form.extras.includes(ex.id)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-slate-50 dark:bg-dark-input border-slate-200 dark:border-white/10 text-slate-500'
                  "
                  class="px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase transition-all group-hover:scale-105"
                >
                  {{ $i18n.locale === "ar" ? ex.name_ar : ex.name_en }} ({{
                    ex.price
                  }}{{ form.currency }})
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4 transition-colors"
        >
          <h2
            class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
          >
            {{ $t("descriptions_rules") }}
          </h2>
          <div class="space-y-4">
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("description_en") }}</label
              >
              <textarea
                v-model="form.description_en"
                rows="2"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-3 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-sm"
              ></textarea>
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("description_ar") }}</label
              >
              <textarea
                v-model="form.description_ar"
                rows="2"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-3 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-sm font-arabic"
              ></textarea>
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                >{{ $t("additional_rules") }}</label
              >
              <textarea
                v-model="form.additional_rules"
                rows="1"
                class="bg-slate-50 dark:bg-dark-input border border-slate-200 dark:border-white/5 p-3 rounded-xl w-full focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none text-sm"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Images & Selections -->
      <div class="space-y-6">
        <!-- Dropdowns -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4 transition-colors"
        >
          <h2
            class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
          >
            {{ $t("specifications") }}
          </h2>
          <div class="space-y-4">
            <BaseSearchableSelect
              v-model="form.owner_id"
              :options="users"
              :label="$t('owner')"
              :placeholder="$t('select_owner')"
            />

            <BaseSearchableSelect
              v-model="form.brand_id"
              :options="brands"
              :label="$t('brand')"
              :placeholder="$t('select_brand')"
              @change="filterCategories"
            />

            <BaseSearchableSelect
              v-model="form.category_id"
              :options="categories"
              :label="$t('category')"
              :placeholder="$t('select_category')"
            />

            <BaseSearchableSelect
              v-model="form.vehicle_type_id"
              :options="vehicleTypes"
              :label="$t('vehicle_type')"
              :placeholder="$t('select_type')"
            />

            <BaseSearchableSelect
              v-model="form.fuel_type_id"
              :options="fuelTypesList"
              :label="$t('fuel_type')"
              :placeholder="$t('select_fuel_type')"
            />

            <BaseSearchableSelect
              v-model="form.transmission_type_id"
              :options="transmissionTypes"
              :label="$t('transmission')"
              :placeholder="$t('select_transmission')"
            />

            <BaseSearchableSelect
              v-model="form.protection_plan_id"
              :options="protectionPlansList"
              :label="$t('protection_plan')"
              :placeholder="$t('no_protection_plan')"
            />
          </div>
        </div>

        <!-- Images Upload -->
        <div
          class="bg-white dark:bg-dark-surface shadow-xl rounded-2xl p-6 border border-slate-100 dark:border-white/5 space-y-4 transition-colors"
        >
          <h2
            class="text-sm font-black uppercase tracking-widest text-primary border-b dark:border-white/5 pb-2"
          >
            {{ $t("photo_gallery") }}
          </h2>
          <div
            class="border-2 border-dashed border-slate-200 dark:border-white/10 rounded-2xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
            @click="$refs.fileInput.click()"
          >
            <div
              class="text-2xl mb-2 group-hover:scale-110 transition-transform"
            >
              📸
            </div>
            <div
              class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
            >
              {{ $t("click_to_upload") }}
            </div>
            <input
              type="file"
              multiple
              @change="handleFiles"
              class="hidden"
              ref="fileInput"
              accept="image/*"
            />
          </div>

          <div class="grid grid-cols-3 gap-2 mt-4">
            <div
              v-for="(img, index) in form.images"
              :key="index"
              class="relative group aspect-square"
            >
              <img
                :src="img.preview"
                class="w-full h-full object-cover rounded-xl border dark:border-white/10"
              />
              <button
                @click="removeImage(index)"
                class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                ×
              </button>
              <div
                class="absolute bottom-1 left-1 right-1 flex items-center justify-center"
              >
                <label
                  class="flex items-center gap-1 bg-black/60 backdrop-blur-md px-1.5 py-0.5 rounded text-[7px] text-white cursor-pointer border border-white/20"
                >
                  <input
                    type="radio"
                    v-model="primaryIndex"
                    :value="index"
                    class="w-2 h-2 accent-primary"
                  />
                  <span>MAIN</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Action -->
        <div class="pt-4">
          <BaseButton
            type="primary"
            @click="submitCar"
            :disabled="submitting"
            class="w-full !rounded-2xl py-4 bg-primary hover:bg-primary-hover text-white transition-all shadow-2xl shadow-primary/40 font-black uppercase tracking-widest text-sm"
          >
            {{ submitting ? $t("processing") : $t("create_car") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseSearchableSelect from "@/components/BaseSearchableSelect.vue";
import carService from "@/services/car.service";
import { alertSuccess, alertError, alertValidation } from "@/utils/alert";

export default {
  components: { BaseButton, BaseSearchableSelect },
  data() {
    return {
      form: {
        model_name: "",
        year: "",
        color: "",
        plate_number: "",
        number_of_seats: 5,
        doors: 4,
        fuel_efficiency: "",
        fuel_policy: "same_level",
        price_per_day: "",
        price_per_week: "",
        price_per_month: "",
        currency: "EGP",
        miles_per_day: 100,
        extra_mile_fee: "",
        delivery_fee_per_km: 0,
        description_en: "",
        description_ar: "",
        additional_rules: "",
        status: "available",
        owner_id: "",
        brand_id: "",
        category_id: "",
        vehicle_type_id: "",
        transmission_type_id: "",
        fuel_type_id: "",
        protection_plan_id: "",
        security_deposit: "",
        has_driver: false,
        location: {
          country_id: "",
          city_id: "",
          latitude: "30.0444",
          longitude: "31.2357",
          state: "Cairo",
          street_address: "",
          postal_code: "",
        },
        availability: {
          available_days: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          advance_notice_hours: 24,
          minimum_trip_days: 1,
          max_trip_days: 30,
        },
        features: [],
        rules: [],
        pickup_options: [],
        elevate_experiences: [],
        extras: [],
        images: [],
      },
      suggestions: [],
      showSuggestions: false,
      searchTimeout: null,
      users: [],
      brands: [],
      allCategories: [],
      categories: [],
      vehicleTypes: [],
      transmissionTypes: [],
      fuelTypesList: [],
      protectionPlansList: [],
      featuresList: [],
      rulesList: [],
      pickupOptionsList: [],
      elevateExperiencesList: [],
      extrasList: [],
      countries: [],
      allCities: [],
      cities: [],
      daysList: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      primaryIndex: 0,
      submitting: false,
      map: null,
      marker: null,
    };
  },
  async mounted() {
    try {
      const res = await carService.getMasterData();
      const master = res.data;
      this.users = master.users;
      this.brands = master.brands;
      this.allCategories = master.categories;
      this.vehicleTypes = master.vehicle_types;
      this.transmissionTypes = master.transmission_types;
      this.fuelTypesList = master.fuel_types || [];
      this.protectionPlansList = master.protection_plans || [];
      this.featuresList = master.features || [];
      this.rulesList = master.car_rules || [];
      this.pickupOptionsList = master.pickup_options || [];
      this.elevateExperiencesList = master.elevate_experiences || [];
      this.extrasList = master.extras || [];
      this.countries = master.countries || [];
      this.allCities = master.cities || [];

      if (this.$route.query.owner_id) {
        this.form.owner_id = this.$route.query.owner_id;
      }

      this.$nextTick(() => {
        this.initMap();
      });
    } catch (error) {
      alertError(this.$t("failed_load"));
    }
  },
  methods: {
    filterCategories() {
      if (!this.form.brand_id) {
        this.categories = [];
        return;
      }
      this.categories = this.allCategories.filter(
        (c) => c.brand_id == this.form.brand_id
      );
    },
    filterCities() {
      this.form.location.city_id = "";
      if (!this.form.location.country_id) {
        this.cities = [];
        return;
      }
      this.cities = this.allCities.filter(
        (c) => c.country_id == this.form.location.country_id
      );
    },
    toggleDay(day) {
      const idx = this.form.availability.available_days.indexOf(day);
      if (idx > -1) this.form.availability.available_days.splice(idx, 1);
      else this.form.availability.available_days.push(day);
    },
    onStreetAddressInput() {
      const query = this.form.location.street_address;
      clearTimeout(this.searchTimeout);
      if (!query || query.length < 3) {
        this.suggestions = [];
        this.showSuggestions = false;
        return;
      }
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              query
            )}&limit=5&accept-language=en`
          );
          const data = await response.json();
          this.suggestions = data;
          this.showSuggestions = data.length > 0;
        } catch (e) {
          this.suggestions = [];
          this.showSuggestions = false;
        }
      }, 400);
    },
    selectSuggestion(item) {
      const lat = parseFloat(item.lat);
      const lon = parseFloat(item.lon);

      this.form.location.street_address = item.display_name;
      this.form.location.latitude = lat.toFixed(6);
      this.form.location.longitude = lon.toFixed(6);

      if (this.map && this.marker) {
        this.map.setView([lat, lon], 15);
        this.marker.setLatLng([lat, lon]);
      }

      this.suggestions = [];
      this.showSuggestions = false;
    },
    onStreetAddressBlur() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 150);
    },
    initMap() {
      if (typeof L === "undefined") {
        setTimeout(this.initMap, 500);
        return;
      }

      const defaultLat = parseFloat(this.form.location.latitude) || 30.0444;
      const defaultLng = parseFloat(this.form.location.longitude) || 31.2357;

      this.map = L.map("map").setView([defaultLat, defaultLng], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(this.map);

      this.marker = L.marker([defaultLat, defaultLng], {
        draggable: true,
      }).addTo(this.map);

      this.marker.on("dragend", (e) => {
        const position = e.target.getLatLng();
        this.form.location.latitude = position.lat.toFixed(6);
        this.form.location.longitude = position.lng.toFixed(6);
      });

      this.map.on("click", (e) => {
        this.marker.setLatLng(e.latlng);
        this.form.location.latitude = e.latlng.lat.toFixed(6);
        this.form.location.longitude = e.latlng.lng.toFixed(6);
      });
    },
    handleFiles(e) {
      const files = Array.from(e.target.files);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.form.images.push({
            file,
            preview: ev.target.result,
          });
        };
        reader.readAsDataURL(file);
      });
    },
    removeImage(index) {
      this.form.images.splice(index, 1);
    },
    async submitCar() {
      if (this.submitting) return;
      this.submitting = true;
      const formData = new FormData();

      Object.keys(this.form).forEach((key) => {
        if (
          ![
            "images",
            "location",
            "availability",
            "features",
            "rules",
            "pickup_options",
            "elevate_experiences",
            "extras",
          ].includes(key)
        ) {
          formData.append(key, this.form[key]);
        }
      });

      formData.append("has_driver", this.form.has_driver ? 1 : 0);
      formData.append(
        "delivery_fee_per_km",
        this.form.delivery_fee_per_km === "" ||
          this.form.delivery_fee_per_km === null
          ? 0
          : this.form.delivery_fee_per_km
      );

      Object.keys(this.form.location).forEach((key) => {
        formData.append(`location[${key}]`, this.form.location[key]);
      });

      Object.keys(this.form.availability).forEach((key) => {
        if (key === "available_days") {
          this.form.availability.available_days.forEach((day) => {
            formData.append("availability[available_days][]", day);
          });
        } else {
          formData.append(`availability[${key}]`, this.form.availability[key]);
        }
      });

      this.form.features.forEach((f) => formData.append("features[]", f));
      this.form.rules.forEach((r) => formData.append("rules[]", r));
      this.form.pickup_options.forEach((p) =>
        formData.append("pickup_options[]", p)
      );
      this.form.elevate_experiences.forEach((e) =>
        formData.append("elevate_experiences[]", e)
      );
      this.form.extras.forEach((ex) => formData.append("extras[]", ex));

      this.form.images.forEach((img, index) => {
        formData.append(`images[${index}][file]`, img.file);
        formData.append(
          `images[${index}][is_primary]`,
          index === this.primaryIndex ? 1 : 0
        );
        formData.append(`images[${index}][order]`, index);
      });

      try {
        await carService.create(formData);
        alertSuccess(this.$t("created_success"));
        this.$router.push("/cars");
      } catch (error) {
        if (error.response?.status === 422) {
          alertValidation(error.response.data.errors);
        } else {
          alertError(error.response?.data?.message || this.$t("failed_create"));
        }
      } finally {
        this.submitting = false;
      }
    },

    toggleSelectAll(listKey, formKey) {
      const allIds = this[listKey].map((item) => item.id);
      const allSelected = allIds.every((id) => this.form[formKey].includes(id));

      if (allSelected) {
        // لو كله متحدد، امسح الكل
        this.form[formKey] = [];
      } else {
        // غير كده، حدد الكل
        this.form[formKey] = [...allIds];
      }
    },

    isAllSelected(listKey, formKey) {
      if (!this[listKey].length) return false;
      return this[listKey].every((item) =>
        this.form[formKey].includes(item.id)
      );
    },
  },
};
</script>

<style scoped>
.font-arabic {
  font-family: "Tajawal", sans-serif;
  direction: rtl;
}
</style>
